import { OrderingDirection } from '@services/common';
import { IAutomationTemplateFilters } from './AutomationTemplate';
import { IAutomationTrigger } from './AutomationTrigger';
import { IAutomationExecution } from './AutomationExecution';
import { ComparisonOperator, LogicalOperator } from './Operator';
import { AutomationVariableIdentifier } from './AutomationVariable';

// WARNING: Do not change the version number without a migration plan
export const DEFAULT_AUTOMATION_VERSION = 1;

export enum AutomationStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  DRAFT = 'DRAFT',
}

export interface IAutomation {
  id: string;
  name: string;
  description?: string;
  clientId: string;
  programId?: number;
  templateId?: string;
  offerId?: string;
  status: AutomationStatus;
  previousStatus?: AutomationStatus;
  triggers: IAutomationTrigger[];
  revision: number;
  version: number;
  metadata: IAutomationMetadata;
  createdDate: Date;
  updatedDate: Date;
  deletedDate?: Date;
  execution?: IAutomationExecution[];
  providerName?: AutomationProvidersEnum;
  providerId?: number;
  isAutomationV2?: boolean;
}

// A blueprint is a "bare" automation that can be used to create a new automation
// TODO(jb) include `revision` as readonly
export interface IAutomationBlueprint
  extends Omit<
    IAutomation,
    | 'id'
    | 'clientId'
    | 'status'
    | 'previousStatus'
    | 'revision'
    | 'version'
    | 'createdDate'
    | 'updatedDate'
    | 'deletedDate'
  > {
  id?: string; // mark optional
}

export interface IAutomationMetadata {
  icon?: string;
}

export interface IAutomationClientFilters {
  id?: string;
}

export interface IAutomationProgramFilters {
  id?: number;
}

export interface IAutomationFilters {
  id?: string;
  status?: AutomationStatus;
  client?: IAutomationClientFilters;
  program?: IAutomationProgramFilters;
  template?: IAutomationTemplateFilters;
  providerName?: AutomationProvidersEnum;
  providerId?: number;
}

export interface IAutomationOrdering {
  name?: OrderingDirection;
  createdDate?: OrderingDirection;
  updatedDate?: OrderingDirection;
}

export const AUTOMATION_ORDERING_FIELDS: (keyof IAutomationOrdering)[] = [
  'name',
  'createdDate',
  'updatedDate',
];

export interface IAutomationCreateOptions {
  publish?: boolean;
}

export interface IAutomationPublishOptions {
  revision?: number;
}

export interface IAutomationCreatePFAOrderParams {
  memberId: number;
  name: string;
  email: string;
  programId: number;
  resourceId: number;
  specURI: string;
  clientId: string;
  workItemId: string;
  brandCatalogId?: number;
  selectionRuleId?: number;
}

export enum OfferAutomationType {
  AUTOMATED_TIER_TOTAL_SALES = 'AUTOMATED_TIER_TOTAL_SALES',
  AUTOMATED_TIER_CONVERSIONS = 'AUTOMATED_TIER_CONVERSIONS',
  AUTOMATED_BOOST = 'AUTOMATED_BOOST',
}

export interface IAutomatedTierAutomationData {
  threshold: number;
  payoutId: number;
}

export enum BoostTriggerType {
  ON_X_CONVERSION = 'ON_X_CONVERSION', // one time
  ON_EVERY_X_CONVERSION = 'ON_EVERY_X_CONVERSION', // repeated
  ON_FIRST_CONVERSION = 'ON_FIRST_CONVERSION', // one time
  ON_X_TOTAL_SALES = 'ON_X_TOTAL_SALES', // one time
  ON_EVERY_X_TOTAL_SALES = 'ON_EVERY_X_TOTAL_SALES', // repeated
  ON_FIRST_X_ORDER_VALUE = 'ON_FIRST_X_ORDER_VALUE', // one time
  ON_EVERY_X_ORDER_VALUE = 'ON_EVERY_X_ORDER_VALUE', // repeated
}

export enum BoostType {
  DISCOUNT_BOOST = 'DISCOUNT_BOOST',
  CASHBACK_BOOST = 'CASHBACK_BOOST',
  PFA_BOOST = 'PFA_BOOST',
}

export enum BoostAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  PAUSE = 'PAUSE',
  REACTIVATE = 'REACTIVATE',
}

export enum BoostEligibilityType {
  INCLUSION = 'INCLUSION',
  EXCLUSION = 'EXCLUSION',
  ADVANCED_SELECTION = 'ADVANCED_SELECTION',
}

export interface IBoostAutomationData {
  boostId: number;
  boostType: BoostType;
  triggerType: BoostTriggerType;
  triggerValue: number;
  action: BoostAction;
  startDate?: Date;
  endDate?: Date;
  boostRedeemLimit?: number;
  automationNodeId?: string;
  status?: string;
  advancedSelection?: {
    operator: LogicalOperator;
    filters: Array<{
      op: ComparisonOperator;
      value: number;
      attribute: string;
      variableId: AutomationVariableIdentifier;
      locked: boolean;
    }>;
  }
}
export interface ICreateAutomationForOfferParams {
  clientId: string;
  type: OfferAutomationType;
  offerId: number;
  automationData?: IAutomatedTierAutomationData[];
  boostAutomationData?: IBoostAutomationData[];
  automationId?: string;
  status?: AutomationStatus; // Default is ACTIVE
}

export enum AutomationProvidersEnum {
  STA_OFFER = 'STA_OFFER',
  STA_OFFER_EMAILS = 'STA_OFFER_EMAILS',
}

export interface IOfferAutomationResponse {
  id: string; // automationId
  boosts: {
    boostId: number;
    automationNodeId: string
  }[];
  status: AutomationStatus;
}

export interface IProgramAddedAutomationPayload {
  programId: number;
  clientId: string;
  instaFollowersFieldId: string,
  vipTagId: number,
  microTagId: number,
  memberId: number,
  memberUpdateType: UpdateMemberActionType,
  delayInMS: number,
  offerId: number,
  productCatalogId: number,
  selectionRuleId: number
}

export const UpdateMemberActionConst = {
  TAG: 'TAG',
  FIELD: 'FIELD',
} as const;

export type UpdateMemberActionType = typeof UpdateMemberActionConst[keyof typeof UpdateMemberActionConst];

export interface IUpdateMemberDataActionMeta {
  type: UpdateMemberActionType,
  fieldId: number,
  fieldValue: string,
}

export interface IAutomationExecutionError {
  message?: string;
  name?: string;
  stack?: string;
  failure?: {
    message?: string;
    cause?: {
      message?: string;
      source?: string;
      stackTrace?: string;
    };
  };
  cause?: {
    message?: string;
    failure?: {
      message?: string;
      source?: string;
      stackTrace?: string;
    };
  };
  activityType?: string;
  activityId?: string;
  retryState?: string;
  identity?: string;
}

export enum AutomationTemplateTypesEnum {
  OFFER_EMAIL_TEMPLATES = 'OFFER_EMAIL_TEMPLATES',
  OFFER_ACTION_TEMPLATES = 'OFFER_ACTION_TEMPLATES',
  PROGRAM_EMAIL_TEMPLATES = 'PROGRAM_EMAIL_TEMPLATES',
}

export type AutomationTemplateType = typeof AutomationTemplateTypesEnum[keyof typeof AutomationTemplateTypesEnum];
